import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  Phone as PhoneIcon,
  PhoneMissed as PhoneMissedIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { leadsAPI, whatsappAPI } from '../../services/api';
import { getViewerTimezone } from '../../utils/dateTimeUtils';
import WhatsAppBubble from './WhatsAppBubble';
import WhatsAppComposer from './WhatsAppComposer';
import WhatsAppTemplatePicker from './WhatsAppTemplatePicker';
import ChatContextPanel from './ChatContextPanel';
import { chatMatchesOwner, inboxOwnerChoices } from './inboxOwners';
import { leadIdForThread, threadMatchesContact } from './leadOnThread';
import { dayKey, dayLabel, foldThreadMessages, listTime } from './threadMessages';
import { initials, wa } from './waTheme';
import useThreadCall from '../../components/Calling/useThreadCall';

const STATUS_LABELS = {
  new: 'New',
  wip_before_demo: 'WIP — before demo',
  demo_scheduled: 'Demo scheduled',
  demo_reschedule_requested: 'Demo reschedule requested',
  demo_completed: 'Demo Completed',
  demo_not_present: 'Demo Not Present',
  wip_after_demo: 'WIP — after demo',
  deferred: 'Deferred',
  enrollment_pending: 'Enrollment Pending',
  won: 'Won',
  lost: 'Lost',
  dropped: 'Dropped',
};

function statusLabel(status) {
  return STATUS_LABELS[status] || status || '';
}

function isLiveItem(item, now) {
  if (item?.window_expires_at) {
    const ms = new Date(item.window_expires_at).getTime();
    if (Number.isFinite(ms)) return ms > now;
  }
  return Boolean(item?.window_open);
}

function windowRemaining(expiresAt, now = Date.now()) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - now;
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const minutes = Math.max(0, Math.floor(ms / 60000));
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  let color = 'error';
  if (hours > 4) color = 'success';
  else if (hours >= 1) color = 'warning';
  return { color, label: hours >= 1 ? `${hours}h ${mins}m` : `${mins}m` };
}

function rowMeta(item, now) {
  const stage = item.leads?.[0]
    ? statusLabel(item.leads[0].status)
    : (item.kind === 'no_lead' ? 'No lead yet' : (item.kind === 'unassigned_lead' ? 'Unassigned lead' : ''));
  const live = isLiveItem(item, now);
  const left = live ? windowRemaining(item.window_expires_at, now) : null;
  const windowText = live ? (left?.label || 'Window open') : 'Window closed';
  return [stage, windowText, item.shared ? 'Shared' : '', item.capi_last_status === 'failed' ? 'Meta update failed' : '']
    .filter(Boolean)
    .join(' · ');
}

export default function WhatsAppChats() {
  const { user, isAdmin, isSales, isSalesCountryScope, getAssignedCountries } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('lg'));
  const tz = getViewerTimezone(user?.timezone);
  const canInbox = isAdmin() || isSalesCountryScope();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const contactId = params.get('contactId');
  const leadId = params.get('leadId');
  const requestedTab = params.get('tab');
  const tab = requestedTab === 'live' || requestedTab === 'followup' || requestedTab === 'landing'
    ? requestedTab
    : (requestedTab === 'unassigned' && canInbox
      ? 'unassigned'
      : (requestedTab === 'all' && canInbox ? 'all' : 'live'));
  const windowParam = leadId || tab === 'landing'
    ? undefined
    : (tab === 'live' ? 'open' : tab === 'followup' ? 'closed' : undefined);
  const bucket = tab === 'landing' ? 'landing' : undefined;
  const [now, setNow] = useState(() => Date.now());
  const [draft, setDraft] = useState('');
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateSheetOpen, setTemplateSheetOpen] = useState(false);
  const [templateOnly, setTemplateOnly] = useState(false);
  const [optedOutLocal, setOptedOutLocal] = useState(false);
  const [pickedLeadId, setPickedLeadId] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkLeadId, setLinkLeadId] = useState('');
  const [showNewPill, setShowNewPill] = useState(false);
  const [search, setSearch] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [callAnchor, setCallAnchor] = useState(null);
  const [moreAnchor, setMoreAnchor] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const noteRef = useRef(null);
  const scrollerRef = useRef(null);
  const stickToBottom = useRef(true);
  const lastInboundRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setTemplateOpen(false);
    setTemplateSheetOpen(false);
    setTemplateOnly(false);
    setOptedOutLocal(false);
  }, [contactId]);

  const countsQuery = useQuery(
    ['whatsappChatCounts'],
    async () => (await whatsappAPI.chatCounts()).data,
    { refetchInterval: 30000, refetchIntervalInBackground: false, retry: false }
  );

  const chatsQuery = useQuery(
    ['whatsappChats', leadId, windowParam || 'all', bucket || 'sales'],
    async () => {
      const res = await whatsappAPI.listChats({
        limit: 50,
        ...(leadId ? { lead_id: leadId } : {}),
        ...(windowParam ? { window: windowParam } : {}),
        ...(bucket ? { bucket } : {}),
      });
      return res.data;
    },
    { enabled: tab !== 'unassigned', refetchInterval: 30000, refetchIntervalInBackground: false, retry: false }
  );

  const unassignedQuery = useQuery(
    ['whatsappUnassigned'],
    async () => (await whatsappAPI.listUnassigned({ limit: 50 })).data,
    { enabled: canInbox && tab === 'unassigned', refetchInterval: 30000, refetchIntervalInBackground: false, retry: false }
  );

  const threadQuery = useQuery(
    ['whatsappThread', contactId],
    async () => (await whatsappAPI.getChat(contactId)).data,
    {
      enabled: !!contactId,
      refetchInterval: 5000,
      refetchIntervalInBackground: false,
      retry: false,
    }
  );

  const rawList = tab === 'unassigned' ? (unassignedQuery.data?.items || []) : (chatsQuery.data?.items || []);
  const list = tab === 'live' ? rawList.filter((item) => isLiveItem(item, now)) : rawList;
  const liveCount = countsQuery.data?.live;
  const followCount = countsQuery.data?.followup;
  const landingCount = countsQuery.data?.landing;
  const disabled = chatsQuery.error?.response?.status === 503;
  const thread = threadQuery.data;
  const canAssign = isAdmin() || isSales();
  const canFilterOwners = isAdmin() || isSalesCountryScope();
  const threadReady = threadMatchesContact(thread, contactId);
  const leadsOnThread = threadReady ? (thread.leads || []) : [];
  const activeLeadId = leadIdForThread(leadsOnThread, pickedLeadId);
  const pickedLead = leadsOnThread.find((lead) => String(lead.id) === String(activeLeadId)) || null;
  const replyContextRef = useRef({ contactId: null, leadId: '' });
  replyContextRef.current = { contactId, leadId: activeLeadId };

  const inboxOwnersQuery = useQuery(
    ['whatsappInboxOwners'],
    async () => (await whatsappAPI.listAssignees()).data,
    { enabled: canFilterOwners, staleTime: 5 * 60 * 1000, retry: false }
  );
  const ownerChoices = useMemo(
    () => inboxOwnerChoices({
      assignees: inboxOwnersQuery.data?.assignees || [],
      chats: list,
      userId: user?.id,
      isAdmin: isAdmin(),
      countries: getAssignedCountries(),
    }),
    [inboxOwnersQuery.data, list, user?.id, isAdmin, getAssignedCountries]
  );

  const assigneesQuery = useQuery(
    ['whatsappAssignees', pickedLead?.id, pickedLead?.country || '', pickedLead?.assigned_to || ''],
    async () => {
      const res = await whatsappAPI.listAssignees({
        ...(pickedLead?.country ? { country: pickedLead.country } : {}),
        ...(pickedLead?.assigned_to ? { include_user_id: pickedLead.assigned_to } : {}),
      });
      return res.data;
    },
    { enabled: !!pickedLead?.id && (wide || detailsOpen), retry: false }
  );

  const assignMutation = useMutation(
    ({ leadId, userId }) => leadsAPI.update(leadId, { assigned_to: userId }),
    {
      onSuccess: () => {
        toast.success('Lead assigned');
        queryClient.invalidateQueries(['whatsappThread', contactId]);
        queryClient.invalidateQueries(['whatsappChats']);
        queryClient.invalidateQueries(['whatsappUnassigned']);
        queryClient.invalidateQueries(['whatsappChatCounts']);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.response?.data?.error || 'Could not assign lead');
      },
    }
  );

  useEffect(() => {
    if (!contactId && leadId && (chatsQuery.data?.items || []).length === 1) {
      const only = chatsQuery.data.items[0];
      setParams((p) => {
        const next = new URLSearchParams(p);
        next.set('contactId', only.contact_id);
        return next;
      }, { replace: true });
    }
  }, [contactId, leadId, chatsQuery.data, setParams]);

  useEffect(() => {
    if (!threadReady) {
      setPickedLeadId((current) => (current ? '' : current));
      return;
    }
    const leads = thread?.leads;
    setPickedLeadId((current) => {
      const nextId = leadIdForThread(leads, current);
      return String(current) === String(nextId) ? current : nextId;
    });
  }, [threadReady, thread]);

  const statusMapQuery = useQuery(
    ['leadStatusSubStatus'],
    async () => (await leadsAPI.getStatusSubStatusMapping()).data?.mapping || {},
    { staleTime: 5 * 60 * 1000, retry: false }
  );

  const statusMutation = useMutation(
    ({ id, status, sub_status }) => leadsAPI.updateStatus(id, { status, sub_status }),
    {
      onSuccess: (_data, vars) => {
        toast.success(vars.subOnly ? 'Substatus updated' : 'Stage updated');
        queryClient.invalidateQueries(['whatsappThread', contactId]);
        queryClient.invalidateQueries(['whatsappChats']);
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || err.response?.data?.message || 'Could not update stage');
      },
    }
  );

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
      if (stickToBottom.current) setShowNewPill(false);
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [contactId]);

  useEffect(() => {
    const inbound = (thread?.messages || []).filter((m) => m.direction === 'in');
    const last = inbound[inbound.length - 1]?.id || null;
    if (last && lastInboundRef.current && last !== lastInboundRef.current && !stickToBottom.current) {
      setShowNewPill(true);
    } else if (stickToBottom.current && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
    lastInboundRef.current = last;
  }, [thread?.messages]);

  const lastMsgId = thread?.messages?.[thread.messages.length - 1]?.id;
  useEffect(() => {
    if (!contactId || !lastMsgId) return undefined;
    whatsappAPI.markRead(contactId, { last_read_message_id: lastMsgId }).then(() => {
      queryClient.invalidateQueries(['whatsappUnread']);
      queryClient.invalidateQueries(['whatsappChats']);
    }).catch(() => {});
    return undefined;
  }, [contactId, lastMsgId, queryClient]);

  const replyMutation = useMutation(
    (payload) => {
      const target = replyContextRef.current;
      const body = typeof payload === 'string' ? { text: payload } : payload;
      return whatsappAPI.reply(target.contactId, {
        ...body,
        ...(target.leadId ? { lead_id: target.leadId } : {}),
      });
    },
    {
      onSuccess: () => {
        setDraft('');
        queryClient.invalidateQueries(['whatsappThread', contactId]);
        queryClient.invalidateQueries(['whatsappChats']);
      },
      onError: (err) => {
        const code = err.response?.data?.code;
        const msg = err.response?.data?.message || 'Could not send';
        toast.error(msg);
        if (code === 'WINDOW_CLOSED') setTemplateOnly(true);
        if (code === 'WINDOW_CLOSED' || code === 'MEDIA_ALREADY_SENT') {
          queryClient.invalidateQueries(['whatsappThread', contactId]);
        }
      },
    }
  );

  const reactMutation = useMutation(
    ({ message, emoji }) => {
      const target = replyContextRef.current;
      return whatsappAPI.react(target.contactId, {
        message_id: message.bp_message_id,
        emoji,
        ...(target.leadId ? { lead_id: target.leadId } : {}),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['whatsappThread', contactId]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Could not react');
        if (err.response?.data?.code === 'WINDOW_CLOSED') {
          queryClient.invalidateQueries(['whatsappThread', contactId]);
        }
      },
    }
  );

  const sendFile = async (file, caption) => {
    const target = replyContextRef.current;
    let uploaded;
    try {
      uploaded = await whatsappAPI.uploadMedia(target.contactId, file);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not upload that file');
      throw err;
    }
    const media = uploaded.data?.media;
    if (!media?.id) {
      toast.error('Upload did not return a file id');
      throw new Error('Upload did not return a file id');
    }
    return replyMutation.mutateAsync({
      text: caption || '',
      media_id: media.id,
      filename: file.name,
      mime_type: file.type || media.mime_type || '',
    });
  };

  const selectContact = (id) => {
    if (id !== contactId) setPickedLeadId('');
    setParams((p) => {
      const next = new URLSearchParams(p);
      next.set('contactId', id);
      return next;
    });
    stickToBottom.current = true;
    setShowNewPill(false);
  };

  const emptyReason = disabled
    ? 'WhatsApp is not enabled in this environment.'
    : (tab === 'landing'
      ? 'No landing chats.'
      : 'No chats yet. Sales will use this after the landing ↔ BP WhatsApp integration is live.');

  const windowOpen = Boolean(thread?.contact?.window_open) && isLiveItem(thread?.contact, now);
  const optedOut = Boolean(thread?.contact?.opted_out) || optedOutLocal;
  const showTextComposer = windowOpen && !templateOnly;
  const showTemplatePanel = !optedOut && (!windowOpen || templateOnly);

  useEffect(() => {
    if (windowOpen) setTemplateOnly(false);
  }, [windowOpen]);

  const refreshAfterTemplate = () => {
    queryClient.invalidateQueries(['whatsappThread', contactId]);
    queryClient.invalidateQueries(['whatsappChats']);
    queryClient.invalidateQueries(['whatsappChatCounts']);
  };

  const foldedMessages = useMemo(
    () => foldThreadMessages(threadReady ? (thread?.messages || []) : []),
    [threadReady, thread]
  );

  const groupedMessages = useMemo(() => {
    const groups = [];
    foldedMessages.forEach((m) => {
      const key = dayKey(m.occurred_at, tz);
      const last = groups[groups.length - 1];
      if (!last || last.day !== key) groups.push({ day: key, at: m.occurred_at, items: [m] });
      else last.items.push(m);
    });
    return groups;
  }, [foldedMessages, tz]);

  const childName = pickedLead?.child_name || thread?.leads?.[0]?.child_name || '';
  const profileName = threadReady ? (thread?.contact?.profile_name || '') : '';
  const chatTitle = childName || profileName || thread?.contact?.e164 || '';
  const call = useThreadCall({
    contactId,
    bpContactId: threadReady ? thread?.contact?.bp_contact_id : undefined,
    leadId: pickedLead?.id || leadId || thread?.leads?.[0]?.id,
    parentName: profileName,
    childName,
    agentName: user?.firstName || '',
    timezone: tz,
  });
  const visibleList = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((item) => {
      if (tab !== 'unassigned' && !chatMatchesOwner(item, ownerFilter, user?.id)) return false;
      if (!q) return true;
      const blob = [
        item.profile_name,
        item.e164,
        item.preview,
        ...(item.leads || []).flatMap((lead) => [lead.child_name, lead.display_id, lead.assigned_name]),
      ].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [list, search, ownerFilter, tab, user?.id]);

  const listLoading = tab === 'unassigned' ? unassignedQuery.isLoading : chatsQuery.isLoading;
  const listEmptyText = (search.trim() || ownerFilter) && list.length
    ? 'No chats match.'
    : emptyReason;
  const remaining = windowOpen ? windowRemaining(thread?.contact?.window_expires_at, now) : null;
  const headerWindow = windowOpen
    ? `Window open${remaining?.label ? ` ${remaining.label}` : ''}`
    : 'Window closed';
  const subtitle = [
    threadReady ? thread?.contact?.e164 : '',
    headerWindow,
    threadReady && thread?.contact?.also_registered ? 'Also registered' : '',
  ].filter(Boolean).join(' · ');
  const missedDay = call.missed?.created_at ? dayKey(call.missed.created_at, tz) : '';
  const missedPlaced = groupedMessages.some((group) => group.day === missedDay);
  const inboxTabs = [
    { value: 'live', label: liveCount != null ? `Live ${liveCount}` : 'Live' },
    { value: 'followup', label: followCount != null ? `Follow-up ${followCount}` : 'Follow-up' },
    { value: 'landing', label: landingCount != null ? `Landing ${landingCount}` : 'Landing' },
    ...(canInbox ? [
      { value: 'all', label: 'All' },
      { value: 'unassigned', label: 'Unassigned' },
    ] : []),
  ];
  const showMore = !wide
    || Boolean(pickedLead?.display_id)
    || (showTextComposer && !optedOut)
    || (tab === 'unassigned' && canInbox);

  const setTab = (value) => {
    setParams((p) => {
      const next = new URLSearchParams(p);
      if (value === 'live') next.delete('tab');
      else next.set('tab', value);
      return next;
    });
  };

  const changeStage = (status) => {
    if (!pickedLead?.id || status === pickedLead.status) return;
    if (status === 'won' && pickedLead.status !== 'won') {
      toast('Open the lead to mark it won. That starts enrollment.');
      return;
    }
    const allowed = statusMapQuery.data?.[status] || [];
    const current = pickedLead.sub_status || '';
    statusMutation.mutate({
      id: pickedLead.id,
      status,
      sub_status: allowed.includes(current) ? current : '',
    });
  };

  const changeSubStatus = (subStatus) => {
    if (!pickedLead?.id || !pickedLead.status) return;
    const next = subStatus || '';
    if (next === (pickedLead.sub_status || '')) return;
    statusMutation.mutate({
      id: pickedLead.id,
      status: pickedLead.status,
      sub_status: next,
      subOnly: true,
    });
  };

  const changeOwner = (userId) => {
    if (!pickedLead?.id || !userId || userId === pickedLead.assigned_to) return;
    assignMutation.mutate({ leadId: pickedLead.id, userId });
  };

  const openLead = () => {
    if (pickedLead?.display_id) navigate(`/leads?search=${encodeURIComponent(pickedLead.display_id)}`);
  };

  const focusRequest = () => {
    setCallAnchor(null);
    if (wide) window.requestAnimationFrame(() => noteRef.current?.focus());
    else setRequestOpen(true);
  };

  const callBack = () => {
    if (call.offer.mode === 'call') {
      call.start();
      return;
    }
    focusRequest();
  };

  const context = (
    <ChatContextPanel
      leads={leadsOnThread}
      pickedLead={pickedLead}
      onPickLead={setPickedLeadId}
      statusOptions={Object.entries(STATUS_LABELS)}
      onStage={changeStage}
      subStatusOptions={statusMapQuery.data?.[pickedLead?.status] || []}
      subStatusKnown={statusMapQuery.isFetched}
      onSubStatus={changeSubStatus}
      stageBusy={statusMutation.isLoading}
      canAssign={canAssign}
      assignees={assigneesQuery.data?.assignees || []}
      assigneesLoading={assigneesQuery.isLoading}
      assigneesError={assigneesQuery.error
        ? (assigneesQuery.error.response?.data?.message || 'Could not load people to assign')
        : ''}
      onOwner={changeOwner}
      ownerBusy={assignMutation.isLoading}
      lastReply={threadReady ? thread?.sharing?.last_reply_by : null}
      tz={tz}
      now={now}
      windowOpen={windowOpen}
      expiresAt={threadReady ? thread?.contact?.window_expires_at : null}
      remainingLabel={remaining?.label || ''}
      call={call}
      onOpenLead={openLead}
      onOpenLeads={() => {
        const phone = threadReady ? (thread?.contact?.e164 || '') : '';
        const name = threadReady ? (thread?.contact?.profile_name || '') : '';
        const next = new URLSearchParams();
        next.set('create', '1');
        if (phone) next.set('phone', phone);
        if (name) next.set('name', name);
        navigate(`/leads?${next.toString()}`);
      }}
      phone={threadReady ? (thread?.contact?.e164 || '') : ''}
      noteRef={noteRef}
      shared={Boolean(threadReady && thread?.sharing?.shared)}
    />
  );

  const missedBanner = call.enabled && call.missed ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
      <Box sx={{
        bgcolor: '#fff',
        borderRadius: 999,
        px: 1.5,
        py: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        boxShadow: '0 1px 2px rgba(11,20,26,.08)',
        maxWidth: '100%',
      }}
      >
        <PhoneMissedIcon sx={{ fontSize: 16, color: wa.meta }} />
        <Typography sx={{ fontSize: 13, color: wa.text }}>
          {`Missed WhatsApp call from ${chatTitle || 'this parent'}`}
        </Typography>
        <Button size="small" onClick={callBack} sx={{ textTransform: 'none', color: wa.green, fontWeight: 700, minWidth: 0 }}>
          Call back
        </Button>
      </Box>
    </Box>
  ) : null;

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 120px)', minHeight: 520, bgcolor: '#fff', border: `1px solid ${wa.border}`, overflow: 'hidden' }}>
      <Box sx={{ width: 340, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#fff', borderRight: `1px solid ${wa.border}` }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 1.75, pb: 1 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: wa.text }}>Inbox</Typography>
          {canFilterOwners ? (
            <TextField
              select
              size="small"
              value={ownerFilter}
              onChange={(event) => setOwnerFilter(event.target.value)}
              aria-label="Inbox owner"
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) => {
                  if (!value) return 'Everyone';
                  if (value === 'mine') return 'Mine';
                  return ownerChoices.find((row) => String(row.id) === String(value))?.name || 'Owner';
                },
              }}
              sx={{
                width: 148,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: 13,
                  bgcolor: ownerFilter ? '#f3f4f6' : '#fff',
                },
                '& .MuiSelect-select': { py: 0.6 },
              }}
            >
              <MenuItem value="">Everyone</MenuItem>
              <MenuItem value="mine">Mine</MenuItem>
              {ownerChoices.map((row) => (
                <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem>
              ))}
            </TextField>
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
              onClick={() => setOwnerFilter((value) => (value === 'mine' ? '' : 'mine'))}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                color: wa.text,
                borderColor: ownerFilter === 'mine' ? wa.text : wa.border,
                fontWeight: ownerFilter === 'mine' ? 700 : 500,
                bgcolor: ownerFilter === 'mine' ? '#f3f4f6' : '#fff',
              }}
            >
              Mine
            </Button>
          )}
        </Stack>
        <Box sx={{ px: 1.5, mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search name, number or lead ID"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: wa.meta, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f6f7f8' } }}
          />
        </Box>
        <Box sx={{ mx: 1.5, mb: 1.25, overflow: 'auto' }}>
          <Box sx={{ p: 0.4, bgcolor: '#f3f4f6', borderRadius: 2, display: 'flex', gap: 0.5, minWidth: '100%', width: 'max-content' }}>
          {inboxTabs.map((item) => {
            const selected = tab === item.value;
            return (
              <Button
                key={item.value}
                onClick={() => setTab(item.value)}
                sx={{
                  flex: '1 0 auto',
                  px: 1.25,
                  py: 0.6,
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  borderRadius: 1.5,
                  fontSize: 13,
                  lineHeight: 1.2,
                  fontWeight: selected ? 700 : 500,
                  color: wa.text,
                  bgcolor: selected ? '#fff' : 'transparent',
                  boxShadow: selected ? '0 1px 2px rgba(11,20,26,.08)' : 'none',
                  '&:hover': { bgcolor: selected ? '#fff' : 'rgba(255,255,255,0.55)' },
                }}
              >
                {item.label}
              </Button>
            );
          })}
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#fff' }}>
          {listLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: wa.green }} /></Box>
          ) : visibleList.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>{listEmptyText}</Typography>
          ) : (
            visibleList.map((item) => {
              const title = item.leads?.[0]?.child_name || item.profile_name || item.e164;
              const whatsappName = item.leads?.[0]?.child_name && item.profile_name ? item.profile_name : '';
              const selected = item.contact_id === contactId;
              const unread = item.unread_count > 0;
              return (
                <Box
                  key={item.contact_id}
                  onClick={() => selectContact(item.contact_id)}
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    cursor: 'pointer',
                    display: 'flex',
                    gap: 1.25,
                    alignItems: 'flex-start',
                    bgcolor: selected ? '#f3f4f6' : 'transparent',
                    '&:hover': { bgcolor: selected ? '#f3f4f6' : '#f7f8f8' },
                  }}
                >
                  <Avatar sx={{ width: 40, height: 40, bgcolor: '#e7eaed', color: '#3d4a54', fontSize: 14, fontWeight: 700 }}>
                    {initials(title)}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1, borderBottom: `1px solid ${wa.border}`, pb: 1.25 }}>
                    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                      <Typography noWrap sx={{ fontSize: 15, color: wa.text, fontWeight: unread ? 700 : 600 }}>
                        {title}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: unread ? wa.unread : wa.meta, flexShrink: 0 }}>
                        {listTime(item.last_at, tz, now)}
                      </Typography>
                    </Stack>
                    {whatsappName && (
                      <Typography noWrap sx={{ fontSize: 12, color: wa.meta }}>{whatsappName}</Typography>
                    )}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Typography noWrap sx={{ fontSize: 13, color: wa.meta, flex: 1 }}>
                        {item.needs_reply ? '• ' : ''}{item.last_direction === 'out' ? 'You: ' : ''}{item.preview || ' '}
                      </Typography>
                      {unread && (
                        <Box sx={{
                          minWidth: 20,
                          height: 20,
                          px: 0.6,
                          borderRadius: 10,
                          bgcolor: wa.unread,
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 600,
                          display: 'grid',
                          placeItems: 'center',
                          flexShrink: 0,
                        }}
                        >
                          {item.has_more_unread ? '99+' : item.unread_count}
                        </Box>
                      )}
                    </Stack>
                    {item.also_registered && (
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: wa.green, mt: 0.25 }}>
                        Also registered
                      </Typography>
                    )}
                    <Typography noWrap sx={{ fontSize: 12, color: wa.meta, mt: 0.25 }}>
                      {rowMeta(item, now)}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, bgcolor: '#f6f5f3' }}>
        {!contactId ? (
          <Box sx={{ m: 'auto', p: 4, textAlign: 'center' }}>
            <Typography sx={{ color: wa.meta, fontSize: 16 }}>Select a conversation</Typography>
          </Box>
        ) : threadQuery.isLoading ? (
          <Box sx={{ m: 'auto' }}><CircularProgress sx={{ color: wa.green }} /></Box>
        ) : threadQuery.error ? (
          <Alert severity="error" sx={{ m: 2 }}>{threadQuery.error.response?.data?.message || 'Could not load thread'}</Alert>
        ) : (
          <>
            <Box sx={{ px: 2, py: 1.25, bgcolor: '#fff', borderBottom: `1px solid ${wa.border}` }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: '#e7eaed', color: '#3d4a54', fontSize: 15, fontWeight: 700 }}>
                    {initials(chatTitle)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: 16, fontWeight: 700, color: wa.text }}>{chatTitle}</Typography>
                    <Typography noWrap sx={{ fontSize: 13, color: wa.meta }}>
                      {childName && profileName ? `${profileName} · ` : ''}
                      {subtitle}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  {call.enabled && (
                    <Button
                      variant="contained"
                      disabled={!call.bpContactId}
                      onClick={(event) => setCallAnchor(event.currentTarget)}
                      startIcon={<PhoneIcon />}
                      endIcon={<ExpandMoreIcon />}
                      sx={{
                        bgcolor: '#157a45',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 700,
                        boxShadow: 'none',
                        '&:hover': { bgcolor: '#11693b', boxShadow: 'none' },
                      }}
                    >
                      Call
                    </Button>
                  )}
                  {showMore && (
                    <IconButton aria-label="Chat actions" onClick={(event) => setMoreAnchor(event.currentTarget)}>
                      <MoreVertIcon />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
              {thread.contact?.capi_last_status === 'failed' && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Meta update failed{thread.contact.capi_last_error ? `: ${thread.contact.capi_last_error}` : ''}.
                </Alert>
              )}
            </Box>
            <Menu
              anchorEl={callAnchor}
              open={Boolean(callAnchor)}
              onClose={() => setCallAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { mt: 0.5, minWidth: 220, borderRadius: 2, p: 0.5 } }}
            >
              <MenuItem
                disabled={call.offer.mode !== 'call' || call.busy}
                onClick={() => {
                  setCallAnchor(null);
                  call.start();
                }}
                sx={{ borderRadius: 1, bgcolor: call.offer.mode === 'call' ? '#f3f4f6' : 'transparent' }}
              >
                Call now
              </MenuItem>
              <MenuItem
                disabled={!call.bpContactId || call.busy || call.offer.mode === 'hidden' || call.offer.mode === 'loading'}
                onClick={focusRequest}
                sx={{ borderRadius: 1 }}
              >
                Request a call from...
              </MenuItem>
              {call.offer.mode === 'hidden' ? (
                <MenuItem
                  disabled={call.busy}
                  onClick={() => {
                    setCallAnchor(null);
                    call.setDoNotCall(false);
                  }}
                  sx={{ borderRadius: 1 }}
                >
                  Allow calls
                </MenuItem>
              ) : (
                <MenuItem
                  disabled={!call.bpContactId || call.busy || call.offer.mode === 'loading'}
                  onClick={() => {
                    setCallAnchor(null);
                    call.setDoNotCall(true);
                  }}
                  sx={{ borderRadius: 1 }}
                >
                  Mark do-not-call
                </MenuItem>
              )}
            </Menu>
            <Menu
              anchorEl={moreAnchor}
              open={Boolean(moreAnchor)}
              onClose={() => setMoreAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {!wide && (
                <MenuItem onClick={() => { setMoreAnchor(null); setDetailsOpen(true); }}>Lead details</MenuItem>
              )}
              {pickedLead?.display_id && (
                <MenuItem onClick={() => { setMoreAnchor(null); openLead(); }}>Open lead</MenuItem>
              )}
              {showTextComposer && !optedOut && (
                <MenuItem onClick={() => { setMoreAnchor(null); setTemplateOpen(true); }}>Send template</MenuItem>
              )}
              {tab === 'unassigned' && canInbox && thread.contact?.match_status === 'unmatched' && (
                <MenuItem onClick={() => { setMoreAnchor(null); setLinkOpen(true); }}>Link lead</MenuItem>
              )}
              {tab === 'unassigned' && canInbox && (
                <MenuItem onClick={async () => {
                  setMoreAnchor(null);
                  await whatsappAPI.dismissUnassigned(contactId);
                  queryClient.invalidateQueries(['whatsappUnassigned']);
                  toast.success('Hidden until the next inbound message');
                }}
                >
                  Dismiss
                </MenuItem>
              )}
            </Menu>

            <Box ref={scrollerRef} sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5, bgcolor: '#f6f5f3', position: 'relative' }}>
              {groupedMessages.map((g) => (
                <Box key={g.day || g.at}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                    <Typography sx={{
                      bgcolor: '#fff',
                      color: wa.icon,
                      fontSize: 12.5,
                      px: 1.25,
                      py: 0.6,
                      borderRadius: 1,
                      boxShadow: '0 1px 0.5px rgba(11,20,26,.13)',
                    }}
                    >
                      {dayLabel(g.at, tz, now)}
                    </Typography>
                  </Box>
                  {g.day === missedDay && missedBanner}
                  {g.items.map((m) => (
                    <WhatsAppBubble
                      key={m.id}
                      message={m}
                      tz={tz}
                      windowOpen={windowOpen}
                      reacting={reactMutation.isLoading}
                      onReact={(message, emoji) => reactMutation.mutate({ message, emoji })}
                      onMediaExpired={() => queryClient.invalidateQueries(['whatsappThread', contactId])}
                    />
                  ))}
                </Box>
              ))}
              {!missedPlaced && missedBanner}
              {showNewPill && (
                <Button
                  size="small"
                  variant="contained"
                  sx={{ position: 'sticky', bottom: 8, left: '50%', transform: 'translateX(-50%)', bgcolor: wa.send, '&:hover': { bgcolor: '#008f72' } }}
                  onClick={() => {
                    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
                    setShowNewPill(false);
                    stickToBottom.current = true;
                  }}
                >
                  New messages ↓
                </Button>
              )}
            </Box>

            {optedOut && (
              <Alert severity="warning" sx={{ borderRadius: 0 }}>Parent opted out of campaigns. Replies inside the 24-hour window are still allowed.</Alert>
            )}
            {showTextComposer && !optedOut && (
              <Box sx={{ px: 2, pt: 1, bgcolor: '#fff', borderTop: `1px solid ${wa.border}` }}>
                <Button size="small" onClick={() => setTemplateOpen(true)} sx={{ color: wa.green }}>
                  Send template
                </Button>
              </Box>
            )}
            {showTextComposer && (
              <WhatsAppComposer
                draft={draft}
                onDraft={setDraft}
                sending={replyMutation.isLoading}
                onRejectFile={(message) => toast.error(message)}
                onSendText={(text) => replyMutation.mutateAsync(text)}
                onSendFile={sendFile}
              />
            )}
            {!showTextComposer && (optedOut || !templateSheetOpen) && (
              <Box sx={{
                px: 2,
                pt: 1.5,
                pb: showTemplatePanel ? 0.5 : 1.5,
                bgcolor: '#fff',
                borderTop: `1px solid ${wa.border}`,
                textAlign: 'center',
              }}
              >
                <Typography sx={{ fontSize: 13, color: wa.meta, mb: (thread.leads || []).length > 0 ? 1 : 0 }}>
                  {optedOut
                    ? 'The 24-hour window is closed. This parent opted out, so a template cannot be sent.'
                    : 'The 24-hour window is closed. A live template is the only message you can send. Photos, files, and reactions wait until the parent messages you.'}
                </Typography>
                {(thread.leads || []).length > 0 && (
                  <Button size="small" onClick={openLead}>Open lead</Button>
                )}
              </Box>
            )}
            {showTemplatePanel && (
              <WhatsAppTemplatePicker
                contactId={contactId}
                leadId={activeLeadId}
                e164={threadReady ? (thread?.contact?.e164 || '') : ''}
                profileName={profileName}
                onActiveChange={setTemplateSheetOpen}
                onSent={refreshAfterTemplate}
                onOptedOut={() => setOptedOutLocal(true)}
              />
            )}
            {templateOpen && showTextComposer && !optedOut && (
              <WhatsAppTemplatePicker
                variant="dialog"
                contactId={contactId}
                leadId={activeLeadId}
                e164={threadReady ? (thread?.contact?.e164 || '') : ''}
                profileName={profileName}
                onClose={() => setTemplateOpen(false)}
                onSent={() => {
                  refreshAfterTemplate();
                  setTemplateOpen(false);
                }}
                onOptedOut={() => {
                  setOptedOutLocal(true);
                  setTemplateOpen(false);
                }}
              />
            )}
          </>
        )}
      </Box>

      {wide && contactId && !threadQuery.isLoading && !threadQuery.error ? (
        <Box sx={{ flexShrink: 0, borderLeft: `1px solid ${wa.border}`, bgcolor: '#fff' }}>
          {context}
        </Box>
      ) : (
        <Drawer anchor="right" open={!wide && detailsOpen} onClose={() => setDetailsOpen(false)}>
          {context}
        </Drawer>
      )}

      <Dialog open={requestOpen} onClose={() => setRequestOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Request a call</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Note for caller"
            fullWidth
            multiline
            minRows={3}
            value={call.reason}
            onChange={(event) => call.editReason(event.target.value)}
            inputProps={{ maxLength: 300 }}
          />
          {call.offer.mode === 'call' && (
            <Typography sx={{ fontSize: 13, color: wa.meta, mt: 1 }}>Calls are allowed. Use Call now.</Typography>
          )}
          {call.offer.mode === 'wait' && (
            <Typography sx={{ fontSize: 13, color: wa.meta, mt: 1 }}>{call.waitText}</Typography>
          )}
          {call.message && (
            <Typography sx={{ fontSize: 13, color: call.tone === 'error' ? 'error' : 'text.secondary', mt: 1 }}>
              {call.message}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestOpen(false)}>Close</Button>
          <Button
            variant="contained"
            disabled={call.busy || !String(call.reason || '').trim() || call.offer.mode !== 'ask'}
            onClick={() => call.ask()}
          >
            Request call
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={linkOpen} onClose={() => setLinkOpen(false)}>
        <DialogTitle>Link this number to a lead</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Lead UUID"
            fullWidth
            value={linkLeadId}
            onChange={(e) => setLinkLeadId(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                await whatsappAPI.linkUnassigned(contactId, { lead_id: linkLeadId.trim() });
                toast.success('Linked');
                setLinkOpen(false);
                queryClient.invalidateQueries(['whatsappUnassigned']);
                queryClient.invalidateQueries(['whatsappThread', contactId]);
              } catch (err) {
                toast.error(err.response?.data?.message || 'Could not link');
              }
            }}
          >
            Link
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
